const normalize = (value, fallback = 'Not specified') => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  if (Array.isArray(value) && value.length === 0) {
    return 'None';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return value;
};

const formatSection = (title, content) => {
  const divider = '='.repeat(title.length);
  return `${title}\n${divider}\n${content.trim()}\n\n`;
};

const buildIdentitySection = (draft) => {
  const content = `
Site Title: ${normalize(draft.siteTitle)}
Site Slug: ${normalize(draft.siteSlug)}
Developer Name: ${normalize(draft.developerName)}
Logo Path: ${normalize(draft.assets?.logoPath)}
Favicon Path: ${normalize(draft.assets?.faviconPath)}
Touch Icon Path: ${normalize(draft.assets?.touchIconPath)}
`;
  return formatSection('IDENTITY', content);
};

const buildLanguageSection = (draft) => {
  let content = `
Primary Language: ${normalize(draft.primaryLanguage)}
Spelling Variant: ${normalize(draft.spellingVariant, 'N/A')}
RTL Layout Required: ${normalize(draft.rtlLayout)}
`;
  
  let instructions = ['Instruction: Respect these settings for all UI labels, buttons, headings, and helper text.'];
  
  if (['en-AU', 'en-UK'].includes(draft.primaryLanguage)) {
    instructions.push('Use British/Australian spelling (e.g., colour, organise, centre).');
  } else if (draft.primaryLanguage === 'en-US') {
    instructions.push('Use US spelling (e.g., color, organize, center).');
  }
  
  if (draft.rtlLayout) {
    instructions.push('Ensure layout supports right-to-left rendering (RTL).');
  }

  content += `\n${instructions.join('\n')}`;
  return formatSection('LANGUAGE & LOCALE', content);
};

const buildConfigurationSection = (draft) => {
  const content = `
Category: ${normalize(draft.category)}
Sub-Category: ${draft.subCategory === 'Other' ? normalize(draft.otherSubCategory) : normalize(draft.subCategory)}
Project Complexity: ${normalize(draft.complexity)}
Framework: React (Vite)
Styling: TailwindCSS
`;
  return formatSection('CONFIGURATION', content);
};

const buildIntegrationsSection = (draft) => {
  const content = `
Code Storage: GitHub
Hosting: Cloudflare Pages
Database: ${normalize(draft.database)}
File Storage: Dropbox
AI: OpenAI
PWA Enabled: ${normalize(draft.pwaEnabled)}
`;
  return formatSection('INTEGRATIONS', content);
};

const buildFeaturesSection = (draft) => {
  const featuresList = Array.isArray(draft.features) && draft.features.length > 0
    ? draft.features.map(f => `- Implement ${f.replace(/([A-Z])/g, ' $1').toLowerCase()}.`).join('\n')
    : 'None specified.';
  const content = `
${featuresList}
`;
  return formatSection('FEATURES', content);
};

const buildPagesSection = (draft) => {
  const pagesList = Array.isArray(draft.pages) && draft.pages.length > 0
    ? draft.pages.map(p => `- Create a standard ${p.replace(/([A-Z])/g, ' $1')} page.`).join('\n')
    : 'None specified.';
  const content = `
${pagesList}
`;
  return formatSection('PAGES', content);
};

const buildBusinessSeoSection = (draft) => {
  const content = `
Company Name: ${normalize(draft.companyName)}
Business Email: ${normalize(draft.businessEmail)}
SEO Keywords: ${normalize(draft.seoKeywords)}
Hide from Search Engines: ${normalize(draft.hideFromSearch)}
`;
  return formatSection('BUSINESS & SEO', content);
};

const buildContextSection = (draft) => {
  const content = `
Project Context: ${normalize(draft.projectContext)}
Constraints: ${normalize(draft.constraints)}
Custom Requirements: ${normalize(draft.customRequirements)}
Build Notes: ${normalize(draft.buildNotes)}
`;
  return formatSection('ADDITIONAL CONTEXT', content);
};

const buildEnvVarSection = () => {
  const vars = [
    'VITE_NCB_INSTANCE_ID',
    'VITE_DROPBOX_APP_KEY',
    'PROMPTLY_PASSWORD',
    'NCB_API_KEY',
    'DROPBOX_APP_KEY',
    'DROPBOX_APP_SECRET',
    'DROPBOX_REFRESH_TOKEN',
    'OPENAI_SECRET_KEY',
  ];
  const content = `The following environment variables must be configured on the server. Do not expose their values.\n${vars.map(v => `- ${v}`).join('\n')}`;
  return formatSection('ENVIRONMENT VARIABLES SUMMARY', content);
};

const buildDeploymentSection = () => {
  const content = `
- Deploy to Cloudflare Pages using the React/Vite preset.
- Build command: npm run build
- Output directory: dist
- Integrate with a GitHub repository for CI/CD.
- All server-side environment variables must be configured as secrets in the Cloudflare Pages deployment settings.
- Ensure Dropbox OAuth is correctly configured for file operations.
- The specified NoCodeBackend instance must be available and configured with the 'promptly_projects' collection.
`;
  return formatSection('DEPLOYMENT REQUIREMENTS', content);
};

export function promptCompiler(projectDraft) {
  // Validation
  const requiredFields = ['siteTitle', 'category', 'primaryLanguage', 'developerName'];
  for (const field of requiredFields) {
    if (!projectDraft[field]) {
      throw new Error(`Validation failed: Missing required field '${field}'.`);
    }
  }

  // Build sections
  let prompt = '';

  prompt += 'Build a production-ready web application using React (Vite), TailwindCSS, Cloudflare Pages, NoCodeBackend, and Dropbox as specified.\n\n';

  prompt += buildIdentitySection(projectDraft);
  prompt += buildLanguageSection(projectDraft);
  prompt += buildConfigurationSection(projectDraft);
  prompt += buildIntegrationsSection(projectDraft);
  prompt += buildFeaturesSection(projectDraft);
  prompt += buildPagesSection(projectDraft);
  prompt += buildBusinessSeoSection(projectDraft);
  prompt += buildContextSection(projectDraft);
  prompt += buildEnvVarSection();
  prompt += buildDeploymentSection();
  
  if (projectDraft.templateId && projectDraft.templateName) {
    prompt += `Based on template: ${projectDraft.templateName} (id: ${projectDraft.templateId}).\n`;
  }

  return prompt.trim();
}